import React, { useState } from 'react';
import { View, StyleSheet, Text } from 'react-native';
import {
  VictoryAxis, VictoryChart, VictoryLabel, VictoryVoronoiContainer, VictoryLine,
} from 'victory';
import Colors from '../constants/Colors';
import { tickValSamples } from './axisUtils';
import { selectedLineColor } from './AirQualityGraph';

const chartTheme = {
  axis: {
    stroke: 'none',
    style: {
      stroke: '#696D70',
      tickLabels: { fill: '#696D70' },
      grid: { stroke: 'none' },
    },
  },
};

type ChartDataPoint = {
  index: number,
  value: number,
  date: string,
  color?: string,
}

interface EmptyGraphProps {
  title: string,
  chartData: ChartDataPoint[][],
}

const MultiLineGraph = ({ title, chartData }: EmptyGraphProps) => {
  const paddingVertical: number = 20;
  const paddingHorizontal: number = 35;
  const xAxisTickCount: number = 7;
  const [dimensions, setDimensions] = useState({ width: 100, height: 100 });

  const Line = (data: ChartDataPoint[], stroke: string, key: number) => (<VictoryLine
    data={data}
    style={{ data: { stroke, strokeWidth: 2 } }}
    x='index'
    y='value'
    key={key}
  />);

  const XAxis = (tickValues, tickFormat, fill: string, dy: number) => <VictoryAxis
    crossAxis={false}
    tickCount={xAxisTickCount}
    {...{ tickValues, tickFormat }}
    fixLabelOverlap
    style={{
      axis: { stroke: 'none' },
      tickLabels: { fontSize: 14 },
    }}
    tickLabelComponent={<VictoryLabel
      {...{ dy }}
      dx={-20}
      style={{
        fill,
        fontSize: 14,
        strokeWidth: 0,
        fontFamily: 'Poppins_400Regular',
        textAnchor: 'center',
      }}
    />}
  />;

  const domainLimit: number = 100;

  const tooltipCircle = (cx: number, value: number, stroke: string, limit: number) => !!value
    && <circle
      r={5}
      strokeWidth={3}
      {...{ stroke, cx }}
      cy={paddingVertical + (dimensions.height - 1 - paddingVertical * 2) * (1 - value / limit)}
      fill='#FFF'
    />;

  class CustomFlyout extends React.Component {
    render() {
      const label = (cy: number, text: string) => <text
        x={containerX + 10}
        y={containerY + cy}
        fontFamily={'Poppins_500Medium'}
        fontSize={12}
        strokeWidth={0}
        fill={Colors.light.hereworksBlack600}>
        {text}
      </text>;

      const { x, y, datum } = this.props as any;
      const dateText: string = datum.date ?? '-';
      const valueText: string = `${'Value'}: ${datum.value ? `${Math.floor(datum.value)}` : '0'}`;
      const containerHeight: number = 60;
      const containerWidth: number = 50
        + Math.max(
          dateText.length,
          valueText.length,
        ) * 6.5;
      const containerX: number = x > containerWidth + 20 ? x - containerWidth - 10 : x + 10;
      let containerY: number = Math.max(Math.min(y - containerHeight, 210 - containerHeight), 10);
      if (!Number.isFinite(containerY)) containerY = 10;

      const dateLabel = (cy: number, text: string) => <text
        x={containerX + 10}
        y={containerY + cy}
        fontFamily={'Poppins_500Medium'}
        fontSize={12}
        strokeWidth={0}
        fill={Colors.light.hereworksBlack600}>
        {text}
      </text>;

      return <svg>
        <defs>
          <filter id={'f3_desk_bookings'} x={-0.5} y={-1} width='200%' height='250%'>
            <feOffset result='offOut' in='SourceGraphic' dx='0' dy='0' />
            <feColorMatrix result='matrixOut'
              in='offOut'
              type='matrix'
              values='0.1 0 0 0 0 0 0.1 0 0 0 0 0 0.1 0 0 0 0 0 0.2 0' />
            <feGaussianBlur result='blurOut' in='matrixOut' stdDeviation='4' />
            <feBlend in='SourceGraphic' in2='blurOut' mode='normal' result='colored' />
          </filter>
        </defs>
        {tooltipCircle(x, datum.value, datum.color ?? Colors.light.hereworksBlack200, domainLimit)}
        <rect
          x={containerX}
          y={containerY}
          width={containerWidth}
          height={containerHeight}
          stroke={selectedLineColor}
          rx={5}
          fill='white'
          filter={'url(#f3_desk_bookings)'}
        />
        {label(45, valueText)}
        {datum.date && dateLabel(20, dateText)}
        <line
          x1={x}
          x2={x}
          y1={paddingVertical}
          y2={dimensions.height - paddingVertical + 15}
          stroke={Colors.light.hereworksBlue100}
          strokeDasharray='10,10'
          strokeWidth={1}
        />
        <line
          x1={0}
          x2={dimensions.width}
          y1={y}
          y2={y}
          stroke={Colors.light.hereworksBlue100}
          strokeDasharray='10,10'
          strokeWidth={1}
        />
      </svg>;
    }
  }

  return <View
    style={styles.chart}
    onLayout={(event) => setDimensions({
      width: event.nativeEvent.layout.width,
      height: event.nativeEvent.layout.height,
    })}
  >
    {title ? <Text style={styles.title}>{title}</Text> : null}
    <VictoryChart
      width={dimensions.width}
      height={dimensions.height}
      theme={chartTheme}
      domain={{ y: [0, domainLimit] }}
      containerComponent={<VictoryVoronoiContainer
        labels={({ datum }) => datum}
        labelComponent={<CustomFlyout />}
      />}
      padding={{
        top: paddingVertical,
        left: paddingHorizontal,
        right: paddingHorizontal,
        bottom: paddingVertical,
      }}
    >
      {XAxis(
        tickValSamples(
          [...Array(chartData[0]?.length).keys()],
          Math.min(7, Math.floor(7 * ((chartData[0]?.length ?? 1) / 1))),
        ),
        (t) => (t),
        Colors.light.hereworksBlack400,
        25,
      )}
      <VictoryAxis
        dependentAxis
        domain={{ y: [0, domainLimit ?? 100] }}
        crossAxis={false}
        tickCount={5}
        tickFormat={(value: number) => Math.round((domainLimit > 2 ? 1 : 10) * value)
          / (domainLimit > 2 ? 1 : 10)}
        fixLabelOverlap
        style={{
          axis: { stroke: 'none' },
          tickLabels: { fontSize: 14 },
          grid: { stroke: Colors.light.hereworksWhite500, strokeWidth: 1, pointerEvents: 'none' },
        }}
        containerComponent={<VictoryVoronoiContainer />}
        tickLabelComponent={<VictoryLabel
          style={{
            fill: Colors.light.hereworksBlack400,
            fontSize: 14,
            strokeWidth: 0,
            fontFamily: 'Poppins_400Regular',
          }}
          dx={-12}
          textAnchor='end'
        />}
      />
      {chartData.map((data, index) => Line(
        data,
        data[0]?.color ?? Colors.light.hereworksDarkBlue,
        index,
      ))}
    </VictoryChart>
    {!(chartData?.length > 0) && <View style={styles.descriptionContainer}>
      <Text style={styles.description}>{'No data available'}</Text>
    </View>}
  </View>;
};

const styles = StyleSheet.create({
  chart: {
    flex: 1,
  },
  title: {
    fontSize: 22,
    lineHeight: 33,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    marginTop: 24,
  },
  descriptionContainer: {
    position: 'absolute',
    alignSelf: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: '100%',
  },
  description: {
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Roboto_400Regular',
    fontSize: 16,
  },
});

export default MultiLineGraph;
